import { useEffect, useState } from "react";
import { Edit3, ImagePlus, Plus, Trash2, X } from "lucide-react";
import { toast } from "react-toastify";
import { http } from "../../axios/axios";
import { GetImageUrl } from "../../helpers/GetImageUrl";

const emptyForm = {
  title: "",
  subtitle: "",
  buttonLink: "",
  pageType: "home",
  isActive: true,
};

const pageLabels = {
  home: "Home",
  products: "Security Products",
  "it-products": "IT Products",
};

const normalizeArtworks = (banner) => {
  if (Array.isArray(banner.artworks) && banner.artworks.length) return banner.artworks;
  if (banner.image) return [{ image: banner.image, imagePublicId: banner.imagePublicId, altText: banner.altText, order: banner.order, isActive: true }];
  return (banner.images || []).map((image, index) => ({ image, altText: banner.altText, order: index + 1, isActive: true }));
};

export default function ProductBannerManager() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [existingArtworks, setExistingArtworks] = useState([]);
  const [newArtworks, setNewArtworks] = useState([]);
  const [removedArtworkIds, setRemovedArtworkIds] = useState([]);

  const inputClass = "w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-blue-500";

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const res = await http.get("/banners", { params: { scope: "product" } });
      setBanners(res.data?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load banners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const clearNewArtworkPreviews = () => {
    newArtworks.forEach((artwork) => {
      if (artwork.preview?.startsWith("blob:")) URL.revokeObjectURL(artwork.preview);
    });
  };

  const resetForm = () => {
    clearNewArtworkPreviews();
    setForm(emptyForm);
    setExistingArtworks([]);
    setNewArtworks([]);
    setRemovedArtworkIds([]);
    setEditId(null);
  };

  const handleArtworkFiles = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    setNewArtworks((prev) => [
      ...prev,
      ...files.map((file, index) => ({
        file,
        preview: URL.createObjectURL(file),
        altText: "",
        order: existingArtworks.length + prev.length + index + 1,
        isActive: true,
      })),
    ]);
    event.target.value = "";
  };

  const handleEdit = (banner) => {
    resetForm();
    setEditId(banner._id);
    setForm({
      title: banner.title || "",
      subtitle: banner.subtitle || "",
      buttonLink: banner.buttonLink || banner.link || "",
      pageType: banner.pageType || "home",
      isActive: banner.isActive ?? true,
    });
    setExistingArtworks(
      normalizeArtworks(banner)
        .sort((first, second) => (Number(first.order) || 0) - (Number(second.order) || 0))
        .map((artwork, index) => ({
          id: artwork._id || artwork.id || `legacy-${index}`,
          image: artwork.image,
          imagePublicId: artwork.imagePublicId || "",
          altText: artwork.altText || "",
          order: artwork.order ?? index + 1,
          isActive: artwork.isActive !== false,
          removable: true,
        })),
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const updateExistingArtwork = (id, changes) => {
    setExistingArtworks((prev) => prev.map((artwork) => artwork.id === id ? { ...artwork, ...changes } : artwork));
  };

  const updateNewArtwork = (index, changes) => {
    setNewArtworks((prev) => prev.map((artwork, artworkIndex) => artworkIndex === index ? { ...artwork, ...changes } : artwork));
  };

  const removeExistingArtwork = (artwork) => {
    if (artwork.removable) setRemovedArtworkIds((prev) => [...prev, artwork.id]);
    setExistingArtworks((prev) => prev.filter((item) => item.id !== artwork.id));
  };

  const removeNewArtwork = (index) => {
    setNewArtworks((prev) => {
      const artwork = prev[index];
      if (artwork?.preview?.startsWith("blob:")) URL.revokeObjectURL(artwork.preview);
      return prev.filter((_, artworkIndex) => artworkIndex !== index);
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!existingArtworks.length && !newArtworks.length) {
      toast.error("At least one artwork image is required");
      return;
    }

    try {
      setSaving(true);
      const data = new FormData();
      data.append("title", form.title.trim());
      data.append("subtitle", form.subtitle.trim());
      data.append("buttonLink", form.buttonLink.trim());
      data.append("link", form.buttonLink.trim());
      data.append("pageType", form.pageType);
      data.append("isActive", String(form.isActive));
      data.append("removedArtworkIds", JSON.stringify(removedArtworkIds));

      const meta = [
        ...existingArtworks.map((artwork) => ({
          id: artwork.removable ? artwork.id : undefined,
          altText: artwork.altText || "",
          order: artwork.order || 0,
          isActive: artwork.isActive !== false,
        })),
        ...newArtworks.map((artwork) => ({
          altText: artwork.altText || "",
          order: artwork.order || 0,
          isActive: artwork.isActive !== false,
        })),
      ];

      data.append("artworksMeta", JSON.stringify(meta));
      newArtworks.forEach((artwork) => data.append("artworks", artwork.file));

      if (editId) {
        await http.put(`/banners/${editId}`, data);
        toast.success("Banner updated");
      } else {
        await http.post("/banners", data);
        toast.success("Banner created");
      }

      resetForm();
      fetchBanners();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to save banner");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (banner) => {
    try {
      const data = new FormData();
      data.append("title", banner.title || "");
      data.append("subtitle", banner.subtitle || "");
      data.append("buttonLink", banner.buttonLink || banner.link || "");
      data.append("link", banner.buttonLink || banner.link || "");
      data.append("pageType", banner.pageType);
      data.append("isActive", String(!banner.isActive));
      data.append("artworksMeta", JSON.stringify(normalizeArtworks(banner).map((artwork) => ({
        id: artwork._id,
        altText: artwork.altText || "",
        order: artwork.order || 0,
        isActive: artwork.isActive !== false,
      }))));
      await http.put(`/banners/${banner._id}`, data);
      fetchBanners();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update status");
    }
  };

  const handleDelete = async (banner) => {
    if (!window.confirm("Delete this banner and all artwork?")) return;

    try {
      await http.delete(`/banners/${banner._id}`);
      toast.success("Banner deleted");
      if (editId === banner._id) resetForm();
      fetchBanners();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to delete banner");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-6 text-white md:p-8">
      <div className="mx-auto max-w-7xl space-y-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-blue-400">Products / IT Products</p>
            <h1 className="mt-2 text-3xl font-black uppercase tracking-tight">Banner Management</h1>
          </div>
          {editId && (
            <button type="button" onClick={resetForm} className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2 text-sm font-bold text-white hover:bg-white/15">
              <X size={16} /> Cancel Edit
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="rounded-3xl border border-white/10 bg-[#141414] p-6 shadow-2xl md:p-8">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-xs font-bold uppercase text-slate-400">Banner Title</label>
              <input value={form.title} onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))} placeholder="Products Main Banner" className={inputClass} />
            </div>
            <div>
              <label className="mb-2 block text-xs font-bold uppercase text-slate-400">Page Type</label>
              <select value={form.pageType} onChange={(event) => setForm((prev) => ({ ...prev, pageType: event.target.value }))} className={inputClass}>
                <option value="home">Home</option>
                <option value="products">Security Products</option>
                <option value="it-products">IT Products</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="mb-2 block text-xs font-bold uppercase text-slate-400">Subtitle / Description</label>
              <textarea value={form.subtitle} onChange={(event) => setForm((prev) => ({ ...prev, subtitle: event.target.value }))} placeholder="Explore our complete range of security and surveillance solutions." rows={3} className={inputClass} />
            </div>
            <div>
              <label className="mb-2 block text-xs font-bold uppercase text-slate-400">Button Link</label>
              <input value={form.buttonLink} onChange={(event) => setForm((prev) => ({ ...prev, buttonLink: event.target.value }))} placeholder="/security-product" className={inputClass} />
            </div>
            <label className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white">
              <input type="checkbox" checked={form.isActive} onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))} className="accent-blue-600" />
              Banner active on frontend
            </label>
          </div>

          <div className="mt-8 rounded-2xl border border-white/10 bg-black/20 p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-black uppercase tracking-[0.18em] text-slate-300">Artworks</h2>
                <p className="mt-1 text-xs text-slate-500">Each active artwork becomes one Swiper slide.</p>
              </div>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700">
                <ImagePlus size={16} /> Add Artwork
                <input type="file" accept="image/*" multiple onChange={handleArtworkFiles} className="hidden" />
              </label>
            </div>

            <div className="grid gap-5 lg:grid-cols-2">
              {existingArtworks.map((artwork) => (
                <article key={artwork.id} className="rounded-2xl border border-white/10 bg-[#101010] p-4">
                  <img src={GetImageUrl(artwork.image)} alt={artwork.altText || "Banner artwork"} className="aspect-[1900/568] w-full rounded-xl bg-black object-contain" />
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <input value={artwork.altText || ""} onChange={(event) => updateExistingArtwork(artwork.id, { altText: event.target.value })} placeholder="Alt text" className={inputClass} />
                    <input type="number" value={artwork.order} onChange={(event) => updateExistingArtwork(artwork.id, { order: event.target.value })} placeholder="Order" className={inputClass} />
                    <label className="flex items-center gap-2 rounded-xl bg-white/5 px-3 py-2 text-sm text-slate-200">
                      <input type="checkbox" checked={artwork.isActive} onChange={(event) => updateExistingArtwork(artwork.id, { isActive: event.target.checked })} className="accent-blue-600" /> Active
                    </label>
                    <button type="button" onClick={() => removeExistingArtwork(artwork)} className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-3 py-2 text-sm font-bold text-white hover:bg-red-700">
                      <Trash2 size={15} /> Remove
                    </button>
                  </div>
                </article>
              ))}

              {newArtworks.map((artwork, index) => (
                <article key={`${artwork.preview}-${index}`} className="rounded-2xl border border-blue-500/30 bg-[#101010] p-4">
                  <img src={artwork.preview} alt="New artwork preview" className="aspect-[1900/568] w-full rounded-xl bg-black object-contain" />
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <input value={artwork.altText || ""} onChange={(event) => updateNewArtwork(index, { altText: event.target.value })} placeholder="Alt text" className={inputClass} />
                    <input type="number" value={artwork.order} onChange={(event) => updateNewArtwork(index, { order: event.target.value })} placeholder="Order" className={inputClass} />
                    <label className="flex items-center gap-2 rounded-xl bg-white/5 px-3 py-2 text-sm text-slate-200">
                      <input type="checkbox" checked={artwork.isActive} onChange={(event) => updateNewArtwork(index, { isActive: event.target.checked })} className="accent-blue-600" /> Active
                    </label>
                    <button type="button" onClick={() => removeNewArtwork(index)} className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-3 py-2 text-sm font-bold text-white hover:bg-red-700">
                      <Trash2 size={15} /> Remove
                    </button>
                  </div>
                </article>
              ))}

              {!existingArtworks.length && !newArtworks.length && (
                <div className="rounded-2xl border border-dashed border-white/10 py-14 text-center text-sm text-slate-500 lg:col-span-2">No artwork selected yet.</div>
              )}
            </div>
          </div>

          <button type="submit" disabled={saving} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-4 text-sm font-black uppercase tracking-widest text-white hover:bg-blue-700 disabled:opacity-60">
            {editId ? <Edit3 size={18} /> : <Plus size={18} />}
            {saving ? "Saving..." : editId ? "Update Banner" : "Save Banner"}
          </button>
        </form>

        <section className="rounded-3xl border border-white/10 bg-[#141414] p-5 md:p-6">
          <div className="mb-5 flex items-center justify-between gap-3">
            <h2 className="text-sm font-black uppercase tracking-[0.2em] text-slate-400">Banner List</h2>
            <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold text-slate-300">{banners.length} records</span>
          </div>

          {loading ? (
            <div className="py-12 text-center text-sm text-slate-400">Loading banners...</div>
          ) : banners.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/10 py-14 text-center text-sm text-slate-500">No product banners found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[920px] text-left text-sm">
                <thead className="text-xs uppercase text-slate-500">
                  <tr className="border-b border-white/10">
                    <th className="py-3 pr-4">Preview</th>
                    <th className="py-3 pr-4">Title</th>
                    <th className="py-3 pr-4">Page</th>
                    <th className="py-3 pr-4">Artworks</th>
                    <th className="py-3 pr-4">Status</th>
                    <th className="py-3 pr-4">Created At</th>
                    <th className="py-3 pr-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {banners.map((banner) => {
                    const artworks = normalizeArtworks(banner);
                    const firstArtwork = artworks[0];

                    return (
                      <tr key={banner._id} className="border-b border-white/5 text-slate-200">
                        <td className="py-4 pr-4">
                          {firstArtwork ? <img src={GetImageUrl(firstArtwork.image)} alt={firstArtwork.altText || banner.title || "Banner"} className="h-14 w-28 rounded-lg bg-black object-contain" /> : "-"}
                        </td>
                        <td className="py-4 pr-4 font-semibold">{banner.title || "Untitled"}</td>
                        <td className="py-4 pr-4">{pageLabels[banner.pageType] || banner.pageType}</td>
                        <td className="py-4 pr-4">{artworks.length}</td>
                        <td className="py-4 pr-4">
                          <button type="button" onClick={() => handleToggle(banner)} className={`rounded-full px-3 py-1 text-xs font-bold ${banner.isActive ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
                            {banner.isActive ? "Active" : "Inactive"}
                          </button>
                        </td>
                        <td className="py-4 pr-4 text-slate-400">{banner.createdAt ? new Date(banner.createdAt).toLocaleDateString() : "-"}</td>
                        <td className="py-4 pr-4">
                          <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => handleEdit(banner)} className="rounded-lg bg-blue-600 p-2 text-white hover:bg-blue-700"><Edit3 size={16} /></button>
                            <button type="button" onClick={() => handleDelete(banner)} className="rounded-lg bg-red-600 p-2 text-white hover:bg-red-700"><Trash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
